import { API_ENDPOINTS } from '@nomiss/constants';
import { apiConfig } from '@nomiss/configs';
import type {
  ChangePhoneResponseDto,
  ConfirmEmailChangeResponseDto,
  UpdateUserDto,
  UserResponseDto,
} from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
  parseApiErrorResponse,
} from './api-request.util';
import { buildApiHeaders, fetchWithAuthRecovery, usersApi } from './sdk-client';
import type { UserProfileV2 } from './users.service';

export type UpdateMyProfileInput = {
  firstName?: string;
  lastName?: string | null;
  profilePhotoId?: string | null;
};

export type ChangePhoneResult = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  profile: UserResponseDto;
};

export type ConfirmEmailChangeResult = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  profile: UserResponseDto;
};

export class ProfileError extends ApiRequestError {
  constructor(
    status: number,
    message: string,
    name = 'ProfileError',
    errorCode?: string,
  ) {
    super(status, message, name, errorCode);
  }
}

/** Map v1 profile (email/phone change responses) to the v2 shape used by Account. */
export function toUserProfileV2(profile: UserResponseDto): UserProfileV2 {
  const firstName = asOptionalString(profile.firstName)?.trim() ?? '';
  const lastName = asOptionalString(profile.lastName)?.trim() ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  const createdAt =
    profile.createdAt instanceof Date
      ? profile.createdAt.toISOString()
      : String(profile.createdAt);

  return {
    id: profile.id,
    email: profile.email,
    fullName,
    phone: asOptionalString(profile.phone),
    profilePhotoId: asOptionalString(profile.profilePhotoId),
    profilePhotoUrl: asOptionalString(profile.profilePhotoUrl),
    isActive: profile.isActive,
    isEmailVerified: profile.isEmailVerified,
    emailVerificationExpiresAt: null,
    mfaEnabled: profile.mfaEnabled,
    roles: profile.roles,
    createdAt,
  };
}

function asOptionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export async function updateMyProfile(
  input: UpdateMyProfileInput,
): Promise<UserResponseDto> {
  const updateUserDto: UpdateUserDto = {};

  const firstName = input.firstName?.trim();
  const lastName =
    input.lastName === null ? null : input.lastName?.trim();

  if (firstName) {
    updateUserDto.firstName = firstName;
  }

  if (lastName === null) {
    updateUserDto.lastName = null;
  } else if (lastName) {
    updateUserDto.lastName = lastName;
  }

  if (input.profilePhotoId !== undefined) {
    updateUserDto.profilePhotoId = input.profilePhotoId;
  }

  return executeSdkRawRequest({
    request: () =>
      usersApi.usersControllerUpdateMyProfileV1Raw({
        updateUserDto,
      }),
    fallback: 'Failed to update profile',
    ErrorClass: ProfileError,
  });
}

/**
 * Pre-OTP eligibility check for change-phone. Call before Firebase SMS so
 * same-as-current / already-registered failures do not trigger billable OTPs.
 */
export async function checkPhoneChangeAvailability(phone: string): Promise<void> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}${API_ENDPOINTS.USER_PHONE_CHECK}`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ phone }),
    },
  );

  if (!response.ok) {
    throw await parseApiErrorResponse(
      response,
      'Failed to validate phone number',
      ProfileError,
    );
  }
}

export async function changeMyPhone(firebaseIdToken: string): Promise<ChangePhoneResult> {
  const data = await executeSdkRawRequest<ChangePhoneResponseDto>({
    request: () =>
      usersApi.phoneChangeControllerChangePhoneV1Raw({
        changePhoneDto: { firebaseIdToken },
      }),
    fallback: 'Failed to change phone number',
    ErrorClass: ProfileError,
  });

  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresIn: data.expiresIn,
    profile: data.profile,
  };
}

export async function requestEmailChange(newEmail: string): Promise<void> {
  await executeSdkRawRequest({
    request: () =>
      usersApi.emailRequestChangeControllerRequestChangeV1Raw({
        requestEmailChangeDto: { newEmail },
      }),
    fallback: 'Failed to request email change',
    ErrorClass: ProfileError,
  });
}

export async function confirmEmailChange(otp: string): Promise<ConfirmEmailChangeResult> {
  const data = await executeSdkRawRequest<ConfirmEmailChangeResponseDto>({
    request: () =>
      usersApi.emailConfirmControllerConfirmV1Raw({
        confirmEmailChangeDto: { otp },
      }),
    fallback: 'Failed to confirm email change',
    ErrorClass: ProfileError,
  });

  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresIn: data.expiresIn,
    profile: data.profile,
  };
}

export async function deleteMyAccount(): Promise<void> {
  await executeSdkRawRequest({
    request: () => usersApi.usersControllerDeleteMyAccountV1Raw(),
    fallback: 'Failed to delete account',
    ErrorClass: ProfileError,
  });
}
